import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Ech0134ModuleStateData } from '../../ech0134-module-state-data';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Ech0134GrundbuchmeldungSearchRequest,
  Ech0134meldungsempfaengerService,
  MeldungsEmpfaengerListEntry
} from '../../../core/grundbuchmeldung-api';
import { simplify } from '../../../shared/functions/meta.functions';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AbstractTypeaheadSearch } from '../../../shared/components/typeahead-search/abstract-typeahead-search';
import { FormsModule } from '@angular/forms';
import { CapFormColumnsDirective } from '../../../shared/form-control/form-columns.directive';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { CapFormRowDirective } from '../../../shared/form-control/cap-form-row.directive';
import { MetaDisablerDirective } from '../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../shared/directives/meta-validator.directive';
import { CapFormControlComponent } from '../../../shared/form-control/form-control.component';
import { DatepickerComponent } from '../../../shared/datepicker/datepicker.component';
import { TagebuchNummerComponent } from '../../../shared/widgets/tagebuch-nummer/tagebuch-nummer.component';
import { CapActionbarComponent } from '../../../core/extension/actionbar/actionbar.component';
import StatusEnum = Ech0134GrundbuchmeldungSearchRequest.StatusEnum;

@Component({
  selector: 'cap-ech0134-grundbuchmeldung-suche',
  templateUrl: './ech0134-grundbuchmeldung-suche.component.html',
  styleUrls: ['./ech0134-grundbuchmeldung-suche.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    ContentHeaderComponent,
    TitleComponent,
    CapFormRowDirective,
    NgbTypeahead,
    MetaDisablerDirective,
    MetaValidatorDirective,
    CapFormControlComponent,
    DatepickerComponent,
    TagebuchNummerComponent,
    CapActionbarComponent,
    TranslatePipe
  ]
})
export class Ech0134GrundbuchmeldungSucheComponent
  extends AbstractTypeaheadSearch<MeldungsEmpfaengerListEntry>
  implements OnInit, AfterViewInit
{
  searchRequest: Ech0134GrundbuchmeldungSearchRequest;
  minSearchLength = 2;
  model: MeldungsEmpfaengerListEntry;
  private subscription: Subscription = new Subscription();

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

  constructor(
    private moduleStateDate: Ech0134ModuleStateData,
    private router: Router,
    private route: ActivatedRoute,
    private empfaengerBackend: Ech0134meldungsempfaengerService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    if (this.moduleStateDate.lastMeldungSearchRequest) {
      this.searchRequest = this.moduleStateDate.lastMeldungSearchRequest;
      if (this.searchRequest.empfaengerId) {
        this.subscription.add(
          this.empfaengerBackend.searchEmpfaenger(this.searchRequest).subscribe((response) => {
            const selectedItem = response.results.find(
              (item: MeldungsEmpfaengerListEntry) => item.id.id === this.searchRequest.empfaengerId.id
            );
            if (selectedItem) {
              this.selectedItem = selectedItem;
              this.model = selectedItem;
              this.value = this.formatEntry(selectedItem);
            }
          })
        );
      }
    } else {
      this.searchRequest = {
        tagebuchNummer: {}
      };
    }
    if (!this.searchRequest.tagebuchNummer) {
      this.searchRequest.tagebuchNummer = {};
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    });
  }

  onSubmit() {
    this.router.navigate(['../list'], {
      relativeTo: this.route,
      queryParams: { request: JSON.stringify(simplify(this.searchRequest)) }
    });
  }

  onReset() {
    this.searchRequest = {
      tagebuchNummer: {}
    };
  }

  onClear() {
    super.onClear();
    this.model = null;
    this.searchRequest.empfaengerId = null;
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<MeldungsEmpfaengerListEntry>) {
    const selectedItem: MeldungsEmpfaengerListEntry = $event.item;
    if (selectedItem) {
      this.selectedItem = selectedItem;
      this.value = this.formatEntry(selectedItem);
      this.searchRequest.empfaengerId = selectedItem.id;
      this.model = selectedItem;
    }
  }

  statusEnums(): Array<StatusEnum> {
    return Object.values(StatusEnum).filter((e) => e !== StatusEnum.NICHT_ERSTELLT);
  }

  translateStatus(e: StatusEnum) {
    return this.translateService.instant('grundbuchmeldung.meldungsstatus.' + e);
  }

  clearEmpfaengerSearch() {
    super.onClear();
    this.model = null;
    this.searchRequest.empfaengerId = null;
  }

  search = (search$: Observable<string>) => {
    return search$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.searchTerm = searchTerm.toLowerCase();
        if (searchTerm?.length >= this.minSearchLength) {
          const data = this.empfaengerBackend.simpleEmpfaengerSearch(searchTerm).pipe(filter((response) => !!response));
          return data;
        } else {
          return of([]);
        }
      }),
      map((data: MeldungsEmpfaengerListEntry[]) => {
        const results = data.filter(this.filterResults).sort(this.sortResults);
        this.results = results;
        return results;
      })
    );
  };

  formatEntry(entry: MeldungsEmpfaengerListEntry): string {
    return entry.sedexId + ' ' + entry.bezeichnung;
  }
}
